const API_BASE = process.env.REACT_APP_API_BASE || "/api";

export async function uploadGalleryPhotos({ files, token }) {
  const form = new FormData();
  for (const file of files) {
    form.append("photos", file);
  }
  const res = await fetch(API_BASE + "/products/upload/gallery", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

async function request(path, opts = {}) {
  const res = await fetch(path, opts);
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

export async function listCategories() {
  return request("/api/categories");
}

export async function createCategory({ name, photoFile, token }) {
  // Send FormData with image file directly
  const form = new FormData();
  form.append("name", name);
  if (photoFile) form.append("photo", photoFile);
  const res = await fetch(API_BASE + "/categories", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}
export async function deleteCategory({ id, token }) {
  const res = await fetch(API_BASE + `/categories/${id}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

// Search suggestions for products and categories
export async function searchSuggestions(query) {
  if (!query || !query.trim()) return { products: [], categories: [] };
  // Try to call a backend endpoint if available, otherwise filter client-side (fallback)
  try {
    const res = await request(
      `/search/suggestions?q=${encodeURIComponent(query)}`,
    );
    // Expected: { products: [...], categories: [...] }
    if (res && (Array.isArray(res.products) || Array.isArray(res.categories))) {
      return {
        products: Array.isArray(res.products) ? res.products : [],
        categories: Array.isArray(res.categories) ? res.categories : [],
      };
    }
    // fallback: try to filter products/categories if not available
    return { products: [], categories: [] };
  } catch (e) {
    return { products: [], categories: [] };
  }
}

export async function getProduct(id) {
  if (!id) return null;
  const res = await request(`/api/products/${id}`);
  // normalize shape
  if (!res) return null;
  return res;
}
export async function listProducts(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await request("/api/products" + (qs ? `?${qs}` : ""));
  // Ensure callers always receive an array
  if (Array.isArray(res)) return res;
  // Some APIs return { products: [...] } or an error object; normalize to empty array on unexpected shape
  if (res && Array.isArray(res.products)) return res.products;
  return [];
}

export async function listProductsBySegment(segment, params = {}) {
  if (!segment) return [];
  const qs = new URLSearchParams(params).toString();
  const res = await request(
    "/api/products" +
      (qs
        ? `?${qs}&segment=${encodeURIComponent(segment)}`
        : `?segment=${encodeURIComponent(segment)}`),
  );
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.products)) return res.products;
  return [];
}

export async function createProduct({ data, token }) {
  return request("/api/products", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
}

export async function uploadPhoto({ file, token }) {
  const form = new FormData();
  form.append("photo", file);
  const res = await fetch(API_BASE + "/products/upload", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

export async function updateProduct({ id, data, token }) {
  return request(`/api/products/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
}

export async function deleteProduct({ id, token }) {
  return request(`/api/products/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function updateCategory({ id, name, photoFile, photo, token }) {
  const form = new FormData();
  if (name) form.append("name", name);
  if (photoFile) form.append("photo", photoFile);
  else if (photo) form.append("photo", photo);
  const res = await fetch(API_BASE + `/categories/${id}`, {
    method: "PUT",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

export async function getAdminStats(token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return request("/api/admin/stats", { method: "GET", headers });
}

export async function getCarousel() {
  const res = await request("/api/carousel");
  if (res && Array.isArray(res.slides)) return res.slides;
  return [];
}

export async function adminGetCarousel(token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await request("/api/admin/carousel", { method: "GET", headers });
  if (res && Array.isArray(res.slides)) return res.slides;
  if (res && Array.isArray(res)) return res;
  return [];
}

export async function adminUploadCarousel({ file, slideNumber, token }) {
  const form = new FormData();
  form.append("image", file);
  form.append("slideNumber", slideNumber);
  const res = await fetch(API_BASE + "/api/admin/carousel", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

export async function adminDeleteCarousel(id, token) {
  return request(`/api/admin/carousel/${id}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getPromo() {
  const res = await request("/api/promo");
  if (res && Array.isArray(res.promos)) return res.promos;
  return [];
}

export async function getTopbarOffers() {
  const res = await request("/api/topbar-offers");
  if (res && Array.isArray(res.offers)) return res.offers;
  return [];
}
